/**
 * Suspense Slide-over Alpine.js Component
 *
 * The bank's suspense ledger as its own account: the balance (negative while
 * the client account is short, positive while it holds money to return) and
 * every movement through it, newest first. Read-only — entries arrive by
 * reconciling a bank line to suspense.
 */

export function suspenseSlideover() {
  return {
    open: false,
    loading: false,
    loadingMore: false,
    error: null,
    zIndex: 100010,

    account: null,
    stats: null,
    transactions: [],
    highlightedTxnId: null,

    page: 1,
    hasMore: false,

    openSlideover() {
      const store = Alpine.store?.('slideovers')
      if (store?.isOpen?.('suspense', 'bank')) {
        store.bringToFront('suspense', 'bank')
        return
      }
      if (store?.getNextZIndex) {
        this.zIndex = store.getNextZIndex()
      }

      this.open = true
      this.transactions = []
      this.page = 1
      this.hasMore = false

      if (store?.register) {
        store.register('suspense', 'bank', this)
      }

      this.fetchData(true)
    },

    close() {
      const store = Alpine.store?.('slideovers')
      if (store) {
        store.unregister('suspense', 'bank')
      }
      this.$nextTick(() => {
        this.open = false
        window.dispatchEvent(new CustomEvent('account-slideover-closed'))
      })
    },

    async fetchData(initial = false) {
      if (initial) {
        this.loading = true
        this.page = 1
        this.transactions = []
      } else {
        this.loadingMore = true
      }
      this.error = null

      try {
        const params = new URLSearchParams()
        params.set('page', this.page)

        const response = await fetch(`/accounting/accounts/suspense.json?${params}`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (!response.ok) throw new Error(`HTTP ${response.status}`)

        const data = await response.json()
        this.account = data.account
        this.stats = data.stats

        if (initial) {
          this.transactions = data.transactions?.lines || []
        } else {
          this.transactions = [...this.transactions, ...(data.transactions?.lines || [])]
        }

        this.hasMore = data.transactions?.pagination?.has_more || false
      } catch (error) {
        console.error('Failed to fetch suspense account:', error)
        this.error = 'Failed to load the suspense account.'
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    loadMore() {
      if (!this.hasMore || this.loadingMore) return
      this.page++
      this.fetchData(false)
    },

    // Formatting helpers (match the deposit scheme slideover)
    formatPence(pence) {
      if (pence == null) return '—'
      const abs = Math.abs(pence)
      const str = (abs / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      return pence < 0 ? `-£${str}` : `£${str}`
    },

    formatSignedPence(pence) {
      if (pence == null) return '—'
      const abs = Math.abs(pence)
      const str = (abs / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      if (pence > 0) return `+£${str}`
      if (pence < 0) return `-£${str}`
      return `£${str}`
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr + 'T00:00:00')
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    },

    txnBarClass(txn) {
      if (txn.amount_pence > 0) return 'bg-green-400'
      if (txn.amount_pence < 0) return 'bg-red-400'
      return 'bg-gray-300'
    },

    amountColorClass(pence) {
      if (pence > 0) return 'text-green-600 dark:text-green-400'
      if (pence < 0) return 'text-red-600 dark:text-red-400'
      return 'text-gray-500'
    }
  }
}
